import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useBackgroundJobs } from '../context/BackgroundJobsContext';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

function formatDateTime(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return '—';
  }
}

const TYPE_CONFIG = {
  encoding:              { label: 'Processing completed', icon: 'M5 13l4 4L19 7',                                                                                                    color: 'bg-emerald-100 text-emerald-600' },
  upload:                { label: 'Photos uploaded',      icon: 'M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12',                                                    color: 'bg-blue-100 text-blue-600'    },
  deletion:              { label: 'Deletion completed',   icon: 'M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16', color: 'bg-rose-100 text-rose-500'    },
  subscription_approved: { label: 'Plan approved',        icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',                                                                     color: 'bg-violet-100 text-violet-600'},
};

const NotifIcon = ({ type }) => {
  const cfg = TYPE_CONFIG[type] || { icon: 'M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9', color: 'bg-slate-100 text-slate-500' };
  return (
    <div className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 ${cfg.color}`}>
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={cfg.icon} />
      </svg>
    </div>
  );
};

const Notifications = () => {
  const { notificationHistory, clearNotificationHistory } = useBackgroundJobs();
  const { userType } = useAuth();
  const [pendingSubscriptionsCount, setPendingSubscriptionsCount] = useState(0);

  useEffect(() => {
    if (userType !== 'admin') return;
    axios.get(`${API_URL}/subscriptions/admin/pending-count`)
      .then((res) => setPendingSubscriptionsCount(res.data?.count ?? 0))
      .catch(() => {});
  }, [userType]);

  return (
    <div className="max-w-2xl mx-auto space-y-4">

      {/* Page header */}
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Notifications</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            {notificationHistory.length === 0
              ? 'No notifications yet'
              : `${notificationHistory.length} notification${notificationHistory.length !== 1 ? 's' : ''}`}
          </p>
        </div>
        {notificationHistory.length > 0 && (
          <button
            type="button"
            onClick={clearNotificationHistory}
            className="text-xs font-semibold text-slate-400 hover:text-rose-500 px-3 py-1.5 rounded-xl hover:bg-rose-50 border border-transparent hover:border-rose-200 transition-all"
          >
            Clear all
          </button>
        )}
      </div>

      {/* Admin: pending subscriptions banner */}
      {userType === 'admin' && pendingSubscriptionsCount > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 bg-amber-50 border border-amber-200 rounded-2xl">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
              <svg className="w-4 h-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <p className="text-sm font-semibold text-amber-900">
              {pendingSubscriptionsCount} subscription{pendingSubscriptionsCount !== 1 ? 's' : ''} pending approval
            </p>
          </div>
          <Link
            to="/subscriptions"
            className="text-xs font-bold px-3 py-1.5 rounded-xl text-amber-800 bg-amber-200 hover:bg-amber-300 border border-amber-300 transition-colors"
          >
            Review →
          </Link>
        </div>
      )}

      {/* Notification list */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        {notificationHistory.length === 0 ? (
          <div className="px-5 py-12 text-center">
            <div className="w-12 h-12 rounded-2xl mx-auto mb-3 flex items-center justify-center bg-slate-100">
              <svg className="w-6 h-6 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
            </div>
            <p className="text-sm font-medium text-slate-600">No notifications yet</p>
            <p className="text-xs text-slate-400 mt-1 max-w-xs mx-auto">Encoding, deletion, and upload events will appear here.</p>
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {notificationHistory.map((n) => (
              <li key={n.id} className="px-4 py-3 flex items-start gap-3 hover:bg-slate-50 transition-colors">
                <NotifIcon type={n.type} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <p className="text-sm font-semibold text-slate-800">
                      {(TYPE_CONFIG[n.type]?.label) || 'Notification'}
                    </p>
                    <p className="text-xs text-slate-400 flex-shrink-0">{formatDateTime(n.createdAt)}</p>
                  </div>
                  <p className="text-xs text-slate-600 mt-0.5 truncate">{n.label}</p>
                  {n.eventName && (
                    <p className="text-xs text-slate-400 mt-0.5">Event: {n.eventName}</p>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default Notifications;
